import { createApp } from "vue";
import i18n from "@/lib/i18n";
import { createPinia } from "pinia";
import { createHead } from "@unhead/vue";
import * as Sentry from "@sentry/vue";
import VueGtag from "vue-gtag-next";
import router from "@/routes";
import { VueQueryPlugin, VueQueryPluginOptions } from "@tanstack/vue-query";
import { env } from "@/lib/variantConfig";
import App from "@/App.vue";

import "@/assets/css/style.css";
import { AxiosError } from "axios";

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof AxiosError && error.response?.status === 404) {
            return false;
          }
          return failureCount < 3;
        },
      },
    },
  },
};

const pinia = createPinia();
const head = createHead();

const app = createApp(App);

if (!import.meta.env.DEV && env.sentry?.dsn) {
  Sentry.init({
    app,
    dsn: env.sentry.dsn,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    tracePropagationTargets: ["localhost"],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

app
  .use(i18n)
  .use(pinia)
  .use(head)
  .use(VueQueryPlugin, vueQueryPluginOptions)
  .use(router);

if (env.googleAnalytics?.measurementId) {
  app.use(VueGtag, {
    property: {
      id: env.googleAnalytics.measurementId,
    },
  });
}

app.mount("#app");
